import { groupBy } from 'lodash-es';
import { useCallback } from 'react';
import { graphql } from 'react-relay';

import useEnumPairsQuery from '../../../hooks/useEnumPairsQuery';
import useLazyLoadQuery from '../../../hooks/useLazyLoadQuery';
import {
  ProductSelectField_productsQuery,
  ProductSelectField_productsQuery$variables,
} from '../../../relay/__generated__/ProductSelectField_productsQuery.graphql';
import { numberWithCommas } from '../../../utils/number';
import SelectField, { SelectFieldProps } from '../../core/SelectField';

const productsForProductSelectField = graphql`
  query ProductSelectField_productsQuery(
    $filters: ProductFilter
    $order: ProductOrder
    $showAdditionalDiscountAmount: Boolean = false
  ) {
    products(filters: $filters, order: $order) {
      edges {
        node {
          id
          sequence
          showTypes
          title
          discountPrice
          additionalDiscountAmount @include(if: $showAdditionalDiscountAmount)
        }
      }
    }
  }
`;

type Props = {
  variables?: ProductSelectField_productsQuery$variables;
} & Omit<SelectFieldProps, 'options'>;

const ProductSelectField = ({ variables, ...props }: Props) => {
  const [{ products }] = useLazyLoadQuery<ProductSelectField_productsQuery>(productsForProductSelectField, {
    showAdditionalDiscountAmount: false,
    ...variables,
    order: { showTypeSequence: 'ASC' },
  });
  const enumPairs = useEnumPairsQuery('ProductShowTypeEnum');

  const getOptions = useCallback(() => {
    const groupedOptionsObject = groupBy(
      products.edges.map(({ node }) => node).filter((node) => !!node),
      ({ showTypes }) => showTypes[0],
    );
    const options = Object.entries(groupedOptionsObject).map(([key, value]) => ({
      label: enumPairs.find(({ key: enumPairKey }) => key === enumPairKey)?.value || '타입 없음',
      options: value.map(({ id, sequence, title, additionalDiscountAmount, discountPrice }) => {
        // return { id, text: [`ID: ${sequence}`, title].join(' | ') };
        return {
          id,
          text: [
            `ID: ${sequence}`,
            `${title} ${
              typeof additionalDiscountAmount === 'number'
                ? `(판매가 ${numberWithCommas(discountPrice)}원 / 콜 추가 할인 금액 ${numberWithCommas(
                    additionalDiscountAmount,
                  )}원)`
                : `(${numberWithCommas(discountPrice)}원)`
            }`,
          ].join(' | '),
        };
      }),
    }));

    return options;
  }, [products]);

  return <SelectField options={getOptions()} {...props} />;
};

export default ProductSelectField;
